/**
 * purchase.js — IAP abstraktionslag
 *
 * Platform-strategi:
 *   Web/browser          → ingen plugin, activate() kaldes direkte (dev-test)
 *   Native + DEV build   → CdvPurchase med Platform.TEST (øjeblikkelig success)
 *   Native + PROD build  → CdvPurchase med App Store + Google Play
 */

import { api } from '../api';

// Brug window.Capacitor global i stedet for ES-import
// (Capacitor sætter window.Capacitor i native-apps; undgår Vite resolve-problemer)
const _Cap = () => window.Capacitor ?? null;

export const PRODUCT_IDS = {
  yearly:        'dk.freshtrack.app.yearly',
  yearlyFamily:  'dk.freshtrack.app.yearly.family',
  monthly:       'dk.freshtrack.app.monthly',
  monthlyFamily: 'dk.freshtrack.app.monthly.family',
};

const ALL_PRODUCT_IDS = Object.values(PRODUCT_IDS);

const IS_NATIVE = _Cap()?.isNativePlatform?.() ?? false;
const IS_DEV    = import.meta.env.DEV;

// Callback der kaldes når et køb er gennemført og verified
let _onPurchased = null;

let _initPromise = null;

// ── Hent CdvPurchase-globals sikkert ────────────────────────────────────────
function cdv() {
  const c = window.CdvPurchase;
  if (!c) throw new Error('cordova-plugin-purchase er ikke loaded endnu');
  return c;
}

// ── Initialiser store — kald én gang ved app-start (i main.jsx) ─────────────
export function initStore(onPurchased) {
  _onPurchased = onPurchased;

  if (!IS_NATIVE) return Promise.resolve(); // Web: intet at gøre
  if (_initPromise) return _initPromise;

  _initPromise = new Promise((resolve, reject) => {
    // Vent til Capacitor-bridge + plugin er klar
    const boot = () => {
      try {
        const { store, ProductType, Platform } = cdv();

        // Vælg platform baseret på OS
        const platformName = _Cap()?.getPlatform?.() ?? 'unknown';
        const platform = platformName === 'ios' ? Platform.APPLE_APPSTORE : Platform.GOOGLE_PLAY;

        // Server-side receipt-validering: pluginnet POSTer kvitteringen til
        // vores backend, som verificerer mod Apple/Google og registrerer købet.
        store.validator = (receipt, callback) => {
          api.validateReceipt(receipt)
            .then(payload => callback(payload))
            .catch(() => callback({ ok: false, code: 6778, message: 'Kunne ikke validere køb' }));
        };

        // Registrér alle produkter
        const subType = ProductType.PAID_SUBSCRIPTION;
        store.register([
          { id: PRODUCT_IDS.yearly,       type: subType, platform },
          { id: PRODUCT_IDS.yearlyFamily,  type: subType, platform },
          { id: PRODUCT_IDS.monthly,       type: subType, platform },
          { id: PRODUCT_IDS.monthlyFamily, type: subType, platform },
        ]);

        // Purchase-pipeline: approved → verify → finish → callback
        store
          .when()
          .approved(transaction => {
            transaction.verify();
          })
          .verified(receipt => {
            receipt.finish();
          })
          .finished(transaction => {
            const boughtId = transaction.products.find(p => ALL_PRODUCT_IDS.includes(p.id))?.id;
            if (boughtId && _onPurchased) {
              _onPurchased({
                productId:     boughtId,
                transactionId: transaction.transactionIdentifier,
                platform:      platformName,
              });
            }
          });

        store.initialize([platform]).then(resolve).catch(reject);
      } catch (err) {
        reject(err);
      }
    };

    // I Capacitor er plugins tilgængelige så snart web-content loader.
    // Men cordova-plugin-purchase bruger stadig 'deviceready' internt —
    // vi prøver begge veje for at undgå race conditions.
    let booted = false;
    const safeBoot = () => { if (!booted) { booted = true; boot(); } };

    document.addEventListener('deviceready', safeBoot, { once: true });

    // Tjek om vi allerede er klar (Capacitor sætter window.CdvPurchase ved load)
    if (window.CdvPurchase) safeBoot();
  });

  return _initPromise;
}

// ── Gennemfør køb ────────────────────────────────────────────────────────────
export async function purchasePremium(productId) {
  if (!IS_NATIVE) {
    // Web/browser: mock-success — PremiumContext kalder activate() direkte
    return { success: true, platform: 'web', productId };
  }

  await _initPromise; // Sikr at store er klar

  const { store } = cdv();
  const product = store.get(productId);

  if (!product) {
    throw new Error(`Produkt '${productId}' ikke fundet i store`);
  }

  const offer = product.getOffer();
  if (!offer) {
    throw new Error('Ingen tilbud tilgængeligt for produktet');
  }

  const error = await offer.order();
  if (error) {
    // CdvPurchase returnerer error-objekt (ikke throw) ved afbrydelse
    if (error.code === cdv().ErrorCode.PAYMENT_CANCELLED) {
      throw new Error('CANCELLED');
    }
    throw new Error(error.message || 'Køb fejlede');
  }

  // Selve success-callbacket kommer via store.when().finished() → onPurchased
  return { success: true };
}

// ── Gendan tidligere køb ─────────────────────────────────────────────────────
export async function restorePurchases() {
  if (!IS_NATIVE) {
    return { restored: false, productId: null };
  }

  await _initPromise;

  const { store } = cdv();
  await store.restorePurchases();

  // Find det første ejede produkt
  const ownedId = ALL_PRODUCT_IDS.find(id => store.get(id)?.owned);

  return { restored: !!ownedId, productId: ownedId ?? null };
}

// ── Er produktet allerede ejet? (til app-start-check) ───────────────────────
export function isPremiumOwned() {
  if (!IS_NATIVE || !_initPromise) return false;
  try {
    const { store } = cdv();
    return ALL_PRODUCT_IDS.some(id => store.get(id)?.owned ?? false);
  } catch {
    return false;
  }
}
